import { useEffect, useState } from 'react';
import { useBulkProcessDayLogsMutation } from 'redux/features/timeLogApi';
import dayjs from 'lib/dayjs';
import { toast } from 'react-toastify';
import useUser from 'hooks/useUser';
import { getAllSundays } from 'utils/common';

import Icon from '../../../components/AppIcon';
import {
  formatDate,
  formatHoursMinutes,
  formatTime,
  formatTimeFromDecimal,
} from '../../../utils/timeTracker';
import DayLogEditModal from '../../dashboard-overview/components/DayLogEditModal';

const MonthlyLogsTable = ({
  monthlyLogs,
  summary,
  onMonthChange,
  userId,
  overtimeEnabled,
  selectedMonth: activeMonth,
  selectedYear: activeYear,
}) => {
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth() + 1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [dayEditModalOpen, setDayEditModalOpen] = useState(false);
  const [selectedDayToEdit, setSelectedDayToEdit] = useState(null);
  const [selectedDayLogs, setSelectedDayLogs] = useState([]);
  const [bulkProcessDayLogs] = useBulkProcessDayLogsMutation();
  const user = useUser();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ].filter((month) => {
    if (selectedYear === currentYear) {
      return month.value <= currentMonth;
    }
    return selectedYear < currentYear;
  });

  const years = Array.from({ length: currentYear - 2010 + 1 }, (_, i) => currentYear - i).filter(
    (year) => year >= 2010 && year <= currentYear,
  );

  useEffect(() => {
    if (activeMonth) {
      setSelectedMonth(activeMonth);
    }
    if (activeYear) {
      setSelectedYear(activeYear);
    }
  }, [activeMonth, activeYear]);

  // Keep month valid when year changes
  useEffect(() => {
    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
      onMonthChange?.(selectedYear, currentMonth);
    }
  }, [selectedYear, selectedMonth, currentYear, currentMonth, onMonthChange]);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    onMonthChange?.(selectedYear, month);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    onMonthChange?.(year, selectedMonth);
  };

  const handlePreviousMonth = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;

    if (newMonth < 1) {
      newMonth = 12;
      newYear = selectedYear - 1;
    }

    if (newYear >= 2010) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      onMonthChange?.(newYear, newMonth);
    }
  };

  const handleNextMonth = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear = selectedYear + 1;
    }

    if (newYear < currentYear || (newYear === currentYear && newMonth <= currentMonth)) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      onMonthChange?.(newYear, newMonth);
    }
  };

  const totalMonthlyHours = summary
    ? summary.totalMonthlyHours
    : monthlyLogs?.reduce((sum, log) => sum + (log?.totalHours || 0), 0) || 0;
  const totalOvertimeHours = summary
    ? summary.totalOvertimeHours
    : monthlyLogs?.reduce((sum, log) => sum + (log?.overtime || 0), 0) || 0;

  const getEventsForDate = (log) => {
    if (log.holiday) {
      return {
        icon: getCompanyEventIcon(log.holidayType),
        label: log.holidayTitle,
        type: 'holiday',
      };
    }
    if (log.leaveType) {
      return {
        icon: getCompanyEventIcon(log.leaveType),
        label: log.leaveType?.replace(/_/g, ' '),
        duration: log.leaveDuration,
        type: 'leave',
      };
    }
    return null;
  };
  const getThisMonthHoliDayCount = () => {
    const total = monthlyLogs?.reduce((sum, log) => {
      if (log?.holidayType === 'FULL_HOLIDAY' || log?.holidayType === 'SATURDAY_OFF') {
        return sum + 1;
      }
      if (log?.holidayType === 'HALF_HOLIDAY') {
        return sum + 0.5;
      }
      return sum;
    }, 0);
    const sundays = getAllSundays(selectedYear, selectedMonth);
    return total + sundays.length;
  };

  const getCompanyEventIcon = (eventType) => {
    switch (eventType) {
      case 'Event Celebration':
        return '🎉';
      case 'Full Holiday':
        return '🏢';
      case 'Half Holiday':
        return '⏰';
      case 'Saturday Off':
        return '🏠';
      case 'FULL_DAY_LEAVE':
        return '🚢';
      case 'HALF_DAY_LEAVE':
        return '🌓';
      case 'SHORT_LEAVE':
        return '⏱️';
      case 'LONG_TIME_LEAVE':
        return '📅';
      case 'MULTIPLE_DAY_LEAVE':
        return '🗓️';
      default:
        return '📅';
    }
  };

  const toggleRow = (date) => {
    if (expandedRow === date) {
      setExpandedRow(null);
    } else {
      setExpandedRow(date);
    }
  };

  const isSameDate = (d1, d2) => {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleBulkDayUpdate = async (payload) => {
    try {
      // Pass userId from props as the first argument property
      await bulkProcessDayLogs({ userId: userId || payload.user_id, ...payload }).unwrap();
      toast.success(
        user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
          ? 'Logs updated successfully'
          : 'Log update requests submitted successfully',
      );
      setDayEditModalOpen(false);
    } catch (error) {
      console.error(error?.data?.message || 'Failed to update logs');
    }
  };

  const gridTemplate = overtimeEnabled
    ? 'grid-cols-[2fr_1fr_1fr_1.2fr_1.3fr_1fr_2fr]'
    : 'grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr_2fr]';

  return (
    <div className="bg-surface border border-border rounded-lg p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-text-primary flex items-center space-x-2 mb-2 sm:mb-0">
            <Icon name="BarChart3" size={20} />
            <span>Monthly Time Logs</span>
          </h2>
          <p className="text-xs sm:text-sm text-text-secondary">
            {months?.find((m) => m?.value === selectedMonth)?.label} {selectedYear}
          </p>
        </div>

        {/* Month/Year Selector with Navigation */}
        <div className="flex items-center space-x-2 sm:space-x-4 mt-3 sm:mt-0">
          {/* Previous Button */}
          <button
            onClick={handlePreviousMonth}
            disabled={selectedYear === 2010 && selectedMonth === 1}
            className="p-2 rounded-lg border border-border hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            title="Previous Month"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>

          {/* Month/Year selectors */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(parseInt(e?.target?.value))}
                className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium text-sm min-w-[120px]"
                style={{ backgroundImage: 'none' }}
              >
                {months?.map((month) => (
                  <option key={month?.value} value={month?.value}>
                    {month?.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
              </div>
            </div>

            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e?.target?.value))}
                className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium text-sm min-w-[80px]"
                style={{ backgroundImage: 'none' }}
              >
                {years?.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
              </div>
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={handleNextMonth}
            disabled={selectedYear === currentYear && selectedMonth === currentMonth}
            className="p-2 rounded-lg border border-border hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            title="Next Month"
          >
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${overtimeEnabled ? 4 : 3} gap-3 sm:gap-4 mb-4 sm:mb-6`}
      >
        <div className="p-3 sm:p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Calendar" size={16} color="var(--color-primary)" />
            <p className="text-sm text-primary-700 font-medium">Total Days</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-primary-800">
            {summary ? summary.totalDays : monthlyLogs?.length || 0} days
          </p>
        </div>

        <div className="p-3 sm:p-4 bg-success-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-success)" />
            <p className="text-sm text-success-700 font-medium">Total time</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-success-800">
            {formatHoursMinutes(totalMonthlyHours)}
          </p>
        </div>

        <div className="p-3 sm:p-4 bg-warning-50 rounded-lg sm:col-span-2 lg:col-span-1">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={16} color="var(--color-warning)" />
            <p className="text-sm text-warning-700 font-medium">Avg Per Day</p>
          </div>
          <p className="text-base sm:text-lg font-semibold text-warning-800">
            {formatHoursMinutes(
              summary
                ? summary.avgWorkingHours
                : monthlyLogs?.length > 0
                  ? totalMonthlyHours / (monthlyLogs?.length - getThisMonthHoliDayCount())
                  : 0,
            )}
          </p>
        </div>

        {overtimeEnabled && (
          <div className="p-3 sm:p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Timer" size={16} color="rgb(147, 51, 234)" />
              <p className="text-sm text-purple-700 font-medium">Overtime</p>
            </div>
            <p className="text-base sm:text-lg font-semibold text-purple-800">
              {formatHoursMinutes(totalOvertimeHours)}
            </p>
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm ">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-6 sticky top-0 z-10">
          <div className={`grid ${gridTemplate} gap-4 items-center`}>
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Date</p>
            </h3>
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Check In</p>
            </h3>
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Check Out</p>
            </h3>
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Working Hours</p>
            </h3>
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Total Hours</p>
            </h3>
            {overtimeEnabled && (
              <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
                <p>Overtime</p>
              </h3>
            )}
            <h3 className="text-[13px] font-bold text-slate-500  tracking-[0.1em]">
              <p>Event/Leave</p>
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="divide-y divide-border min-h-[200px] sm:min-h-[300px] lg:min-h-[400px] max-h-[600px] lg:max-h-[800px] overflow-y-auto">
          {!monthlyLogs || monthlyLogs?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 border border-slate-100">
                <Icon name="FileX" size={28} className="text-slate-300" />
              </div>
              <p className="text-sm font-bold text-slate-900 uppercase tracking-widest">
                No Records Found
              </p>
              <p className="text-xs text-slate-400 mt-1 font-medium">
                No activity logs available for{' '}
                {months?.find((m) => m?.value === selectedMonth)?.label}, {selectedYear}
              </p>
            </div>
          ) : (
            monthlyLogs?.map((log) => {
              const rowOpen = expandedRow === log?.date;
              const isToday = isSameDate(log?.date, currentDate);
              const eventInfo = getEventsForDate(log);
              const isForceCheckout = log?.forceCheckout;
              return (
                <div
                  key={log?.date}
                  className={`border-l-4
                    group transition-all duration-200
                    ${rowOpen ? 'bg-primary-50/30 border-primary-400' : 'bg-white hover:bg-slate-50 border-transparent'}
                    `}
                >
                  {/* Main row */}
                  <div
                    className={`px-4 py-3.5 transition-colors duration-200 cursor-pointer ${isForceCheckout ? 'bg-red-50/50' : ''}`}
                    onClick={() => toggleRow(log?.date)}
                  >
                    <div className={`grid ${gridTemplate} gap-4 items-center`}>
                      {/* Date + expand icon */}
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <button
                            type="button"
                            className={`
                              flex justify-center items-center w-8 h-8 rounded-full
                              border transition-all duration-200
                              ${rowOpen ? 'bg-white border-primary-200 shadow-sm text-primary-600' : 'bg-slate-50 border-slate-200 text-slate-400 group-hover:border-slate-300 group-hover:text-slate-600'}
                            `}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(log?.date);
                            }}
                          >
                            <Icon size={16} name={rowOpen ? 'ChevronUp' : 'ChevronDown'} />
                          </button>
                        </div>

                        <div>
                          <p
                            className={`text-xs sm:text-sm font-medium ${
                              isToday ? 'text-primary-700' : 'text-text-primary'
                            } `}
                          >
                            {formatDate(log?.date)}
                            {/* Admin side: No pending indicators */}
                            {isToday && (
                              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 font-semibold">
                                Today
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {new Date(log.date)?.toLocaleDateString('en-US', {
                              weekday: 'short',
                            })}
                          </p>
                        </div>
                      </div>

                      {/* IN */}
                      <div className="text-sm font-medium text-slate-600">
                        {log?.firstCheckIn ? formatTime(log?.firstCheckIn) : '--:--'}
                      </div>

                      {/* OUT */}
                      <div className="text-sm font-medium text-slate-600">
                        {log?.lastCheckOut ? formatTime(log?.lastCheckOut) : '--:--'}
                      </div>

                      {/* LUNCH + WORKING */}
                      <div className="flex">
                        <span className="inline-flex w-[80px] justify-center  items-center px-2 py-1 rounded-md bg-slate-50 text-slate-500 text-[13px] font-bold border border-slate-100  tracking-tight">
                          {log?.working_hours > 0 ? formatHoursMinutes(log.working_hours) : '--'}
                        </span>
                      </div>

                      {/* TIME */}
                      <div className="flex">
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-lg inline-flex justify-center min-w-[70px] ${
                            log?.totalHours >= (log?.isHalfdayHoliday ? 4.5 : 9)
                              ? 'text-emerald-700 bg-emerald-50 border border-emerald-100'
                              : log?.totalHours >= (log?.isHalfdayHoliday ? 3.5 : 7)
                                ? 'text-amber-700 bg-amber-50 border border-amber-100'
                                : 'text-rose-700 bg-rose-50 border border-rose-100'
                          } `}
                        >
                          {formatHoursMinutes(log?.totalHours)}
                        </span>
                      </div>

                      {/* OVERTIME */}
                      {overtimeEnabled && (
                        <div className="flex">
                          {log?.overtime && log.overtime > 0 ? (
                            <span className="text-xs font-bold px-2.5 py-1 rounded-lg text-indigo-700 bg-indigo-50 border border-indigo-100 min-w-[70px] inline-flex justify-center">
                              {formatHoursMinutes(log.overtime)}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-slate-400 px-2.5 py-1 inline-flex justify-center min-w-[70px] bg-slate-50 border border-slate-100 rounded-lg">
                              --
                            </span>
                          )}
                        </div>
                      )}

                      {/* EVENT/LEAVE */}
                      <div className="flex items-center">
                        {eventInfo ? (
                          <span
                            title={
                              eventInfo.type === 'leave'
                                ? `Duration: ${eventInfo.duration} ${eventInfo.duration > 1 ? 'days' : 'day'}`
                                : ''
                            }
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${eventInfo.type === 'leave' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-slate-50 text-slate-600 border-slate-100'} border`}
                          >
                            <span className="text-base leading-none translate-y-[1px]">
                              {eventInfo.icon}
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-tight truncate max-w-[120px]">
                              {eventInfo.label}
                            </span>
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded section: Overtime + events + log details (from RTK) */}
                  {rowOpen && (
                    <div className="bg-slate-50/50 px-6 py-6 border-t border-slate-100 animate-slideDown">
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2">
                          <Icon name="ClipboardList" size={18} className="text-slate-400" />
                          <p className="font-bold text-xs uppercase tracking-[0.15em] text-slate-500">
                            Log Session Details
                          </p>
                        </div>
                        <button
                          disabled={log?.is_locked}
                          onClick={() => {
                            setSelectedDayToEdit(log?.date);
                            setSelectedDayLogs(
                              log?.fullDayLogs?.filter((detail) => !detail.tempId) || [],
                            );
                            setDayEditModalOpen(true);
                          }}
                          title={log?.is_locked ? 'Log is locked' : 'Edit Log'}
                          type="button"
                          className={`flex ${log?.is_locked ? 'opacity-50 cursor-not-allowed' : ''} items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-primary-200 bg-white text-[11px] font-bold uppercase tracking-wider text-primary-600 hover:bg-primary-50 transition-all shadow-sm`}
                        >
                          <Icon name="Edit3" size={14} />
                          <span>Edit</span>
                        </button>
                      </div>

                      {log?.fullDayLogs?.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-white/50 rounded-xl border border-dashed border-slate-200">
                          <Icon name="Ghost" size={24} className="text-slate-300 mb-2" />
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">
                            No active sessions recorded
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-4">
                          {log?.fullDayLogs
                            ?.filter((detail) => !detail.tempId)
                            .map((detail, index) => {
                              let breakBetween = '';
                              const nextLog = log?.fullDayLogs[index + 1];
                              if (nextLog) {
                                const breakDuration = dayjs(nextLog.check_in).diff(
                                  dayjs(detail.check_out),
                                  'minutes',
                                );
                                breakBetween = formatTimeFromDecimal(breakDuration / 60);
                              }

                              return (
                                <div
                                  key={detail.id || detail.tempId || index}
                                  className={`group/detail bg-white rounded-xl border ${detail.logUpdateRequest ? 'border-primary-200' : 'border-slate-200'} p-4 shadow-sm hover:border-primary-300 transition-all duration-200 relative overflow-hidden`}
                                >
                                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-center">
                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Check In
                                      </p>
                                      <p className="text-sm font-bold text-slate-700">
                                        {detail.check_in ? formatTime(detail.check_in) : '--:--'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Check Out
                                      </p>
                                      <p className="text-sm font-bold text-slate-700">
                                        {detail.check_out ? formatTime(detail.check_out) : '--:--'}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Duration
                                      </p>
                                      <p className="text-sm font-bold text-emerald-600">
                                        {formatHoursMinutes(detail.total_hours || 0)}
                                      </p>
                                    </div>

                                    <div>
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Reason
                                      </p>
                                      <p
                                        className="text-xs font-medium text-slate-600 truncate"
                                        title={detail.reason}
                                      >
                                        {detail.reason || 'Regular Session'}
                                        {breakBetween && (
                                          <span className="text-amber-500 ml-1 font-bold">
                                            ({breakBetween} Break)
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {dayEditModalOpen && (
        <DayLogEditModal
          isOpen={dayEditModalOpen}
          onClose={() => {
            setDayEditModalOpen(false);
            setSelectedDayToEdit(null);
            setSelectedDayLogs([]);
          }}
          date={selectedDayToEdit}
          logs={selectedDayLogs}
          onSubmit={handleBulkDayUpdate}
          user={user}
          targetUserId={userId}
        />
      )}
    </div>
  );
};

export default MonthlyLogsTable;
